import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const testimonials = [{
  quote: 'Después de años dando clases de polo sin un sistema ordenado, este material cambió completamente mi forma de enseñar. Mis alumnos progresan semana a semana.',
  name: 'Carlos Méndez',
  details: '44 años, Buenos Aires'
}, {
  quote: 'Siempre armaba las clases el sábado a la noche. Con los tomos y el resolucionario ya incluidos, ese problema desapareció. Una inversión que se paga sola.',
  name: 'Valentina Ríos',
  details: '38 años, Córdoba'
}, {
  quote: 'Los diagramas tácticos y los guiones de corrección son exactamente lo que necesitaba. Ahora sé cómo explicarle al alumno qué hizo mal sin que se frustre.',
  name: 'Matías Herrera',
  details: '35 años, Rosario'
}, {
  quote: 'Recién empiezo a dar clases de polo y estaba perdido con la planificación del año. El Plan Maestro 12 Meses me salvó. Supe exactamente qué dar cada mes.',
  name: 'Sofía Aguilar',
  details: '27 años, La Plata'
}, {
  quote: 'El Tomo de Técnica Ecuestre es una joya. Las progresiones para enseñar el control con una mano me resolvieron un problema que tenía con mis alumnos iniciales.',
  name: 'Gonzalo Etcheverry',
  details: '41 años, Pilar'
}, {
  quote: 'Lo que más valoro son los tests físicos y las rúbricas de evaluación. Por fin puedo darle a los padres un informe profesional y objetivo.',
  name: 'Lucía Fernández',
  details: '32 años, San Isidro'
}, {
  quote: 'Compré el plan completo dudando un poco, pero superó mis expectativas. La calidad de los diagramas y lo bien explicado que está el sistema 1-3 me voló la cabeza.',
  name: 'Facundo Zavaleta',
  details: '48 años, Mendoza'
}, {
  quote: 'Tengo una escuelita de polo en el interior y a veces es difícil conseguir buen material didáctico. Esto es exactamente lo que necesitaba para profesionalizar mis clases.',
  name: 'Ignacio Pueyrredón',
  details: '39 años, Tandil'
}];
const column1 = testimonials.slice(0, 4);
const column2 = testimonials.slice(4, 8);
function TestimonialCard({
  testimonial
}: {
  testimonial: (typeof testimonials)[0];
}) {
  return <div className="glass-card rounded-xl p-4 md:p-5 relative shrink-0 w-[280px] md:w-auto" data-id="element-149">
      <Quote className="absolute top-3 right-3 w-5 h-5 text-polo-gold/20" data-id="element-150" />
      <div className="flex text-polo-gold mb-2" data-id="element-151">
        {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" data-id="element-152" />)}
      </div>
      <p className="text-xs md:text-sm text-gray-300 mb-3 italic leading-relaxed relative z-10" data-id="element-153">
        "{testimonial.quote}"
      </p>
      <div data-id="element-154">
        <p className="font-bold text-white text-sm" data-id="element-155">{testimonial.name}</p>
        <p className="text-xs text-polo-goldLight" data-id="element-156">{testimonial.details}</p>
      </div>
    </div>;
}
export function TestimonialsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth'
      });
    }
  };
  return <section className="py-10 md:py-20 relative" data-id="element-157">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-158">
        <div className="text-center mb-6 md:mb-10" data-id="element-159">
          <h2 className="text-3xl md:text-4xl font-bold mb-2 md:mb-4" data-id="element-160">
            Lo que dicen otros docentes
          </h2>
          <p className="text-base md:text-lg text-gray-400" data-id="element-161">
            Docentes de todo el país ya transformaron sus clases
          </p>
        </div>

        {/* Mobile: horizontal scrollable row */}
        <div className="md:hidden relative" data-id="element-162">
          <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-163">
            {testimonials.map((testimonial, index) => <div key={index} className="snap-start" data-id="element-164">
                <TestimonialCard testimonial={testimonial} data-id="element-165" />
              </div>)}
          </div>
          <div className="flex justify-center gap-3 mt-3" data-id="element-166">
            <button onClick={() => scroll('left')} className="w-9 h-9 rounded-full glass-card flex items-center justify-center text-polo-gold border border-white/10 active:scale-95" data-id="element-167">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-168" />
            </button>
            <button onClick={() => scroll('right')} className="w-9 h-9 rounded-full glass-card flex items-center justify-center text-polo-gold border border-white/10 active:scale-95" data-id="element-169">
              <ChevronRightIcon className="w-4 h-4" data-id="element-170" />
            </button>
          </div>
        </div>

        {/* Desktop: 2 columns of 4 */}
        <div className="hidden md:grid md:grid-cols-2 gap-5" data-id="element-171">
          <div className="space-y-4" data-id="element-172">
            {column1.map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} data-id="element-173">
                <TestimonialCard testimonial={testimonial} data-id="element-174" />
              </motion.div>)}
          </div>
          <div className="space-y-4" data-id="element-175">
            {column2.map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08 + 0.1
          }} data-id="element-176">
                <TestimonialCard testimonial={testimonial} data-id="element-177" />
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}